import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Share2, Plus, Trash2, Send, X, Edit2, Clock,
  CheckCircle, AlertCircle, Loader, Link2, Image as ImageIcon,
  Facebook, Twitter, Linkedin, Instagram,
  ChevronDown, RefreshCw, Eye,
} from "lucide-react";
import { apiUrl } from "../../../utils/config";

// ── Constants ─────────────────────────────────────────────────────────────

const PLATFORMS = [
  { key: "facebook",  label: "Facebook",  Icon: Facebook,  color: "bg-blue-600",   ring: "ring-blue-300",  text: "text-blue-600"  },
  { key: "instagram", label: "Instagram", Icon: Instagram, color: "bg-pink-600",   ring: "ring-pink-300",  text: "text-pink-600"  },
  { key: "x",         label: "X",         Icon: Twitter,   color: "bg-slate-900",  ring: "ring-slate-300", text: "text-slate-800" },
  { key: "linkedin",  label: "LinkedIn",  Icon: Linkedin,  color: "bg-sky-700",    ring: "ring-sky-300",   text: "text-sky-700"   },
];

const STATUS_BADGE = {
  draft:     "bg-gray-100 text-gray-600",
  scheduled: "bg-yellow-100 text-yellow-700",
  published: "bg-emerald-100 text-emerald-700",
  failed:    "bg-red-100 text-red-700",
};

const STATUS_ICON = {
  draft:     <Clock size={13} />,
  scheduled: <Clock size={13} />,
  published: <CheckCircle size={13} />,
  failed:    <AlertCircle size={13} />,
};

const EMPTY_POST = { content: "", image_url: "", link: "", platforms: [], scheduled_at: "" };
const EMPTY_ACCOUNT = { account_name: "", platform_user_id: "", access_token: "" };

// ── Small helpers ─────────────────────────────────────────────────────────

const inp = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300";
const btn = (extra = "") =>
  `inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${extra}`;

const Drawer = ({ open, onClose, title, children }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex">
      <div className="flex-1 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-lg bg-white h-full shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-900">{title}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">{children}</div>
      </div>
    </div>
  );
};

const Field = ({ label, children, hint }) => (
  <div className="space-y-1">
    <label className="block text-sm font-medium text-gray-700">{label}</label>
    {children}
    {hint && <p className="text-xs text-gray-400">{hint}</p>}
  </div>
);

const PlatformChip = ({ platform, selected, onClick }) => {
  const p = PLATFORMS.find((x) => x.key === platform);
  if (!p) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all
        ${selected
          ? `${p.color} text-white border-transparent shadow`
          : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
        }`}
    >
      <p.Icon size={13} />
      {p.label}
    </button>
  );
};

const Toast = ({ msg, type }) => {
  if (!msg) return null;
  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-white text-sm font-medium
      ${type === "error" ? "bg-red-500" : "bg-emerald-500"}`}>
      {type === "error" ? <AlertCircle size={16} /> : <CheckCircle size={16} />}
      {msg}
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────

export default function SocialMediaPage() {
  const [accounts, setAccounts]   = useState([]);
  const [posts, setPosts]         = useState([]);
  const [tab, setTab]             = useState("posts"); // posts | accounts
  const [loading, setLoading]     = useState(true);
  const [toast, setToast]         = useState({ msg: "", type: "" });

  // post drawer
  const [postDrawer, setPostDrawer]   = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [postForm, setPostForm]       = useState(EMPTY_POST);
  const [saving, setSaving]           = useState(false);
  const [publishing, setPublishing]   = useState(null); // post id being published

  // account drawer
  const [accountDrawer, setAccountDrawer]   = useState(false);
  const [accountPlatform, setAccountPlatform] = useState("");
  const [accountForm, setAccountForm]         = useState(EMPTY_ACCOUNT);
  const [savingAccount, setSavingAccount]     = useState(false);

  // preview modal
  const [preview, setPreview] = useState(null);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: "", type: "" }), 3000);
  };

  // ── Load ────────────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [accRes, postsRes] = await Promise.all([
        axios.get(`${apiUrl}/social-media/accounts`),
        axios.get(`${apiUrl}/social-media/posts`),
      ]);
      if (accRes.data.status)   setAccounts(accRes.data.list);
      if (postsRes.data.status) setPosts(postsRes.data.list);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Post CRUD ────────────────────────────────────────────────────────────

  const openAdd = () => {
    setEditingPost(null);
    setPostForm(EMPTY_POST);
    setPostDrawer(true);
  };

  const openEdit = (post) => {
    setEditingPost(post);
    setPostForm({
      content:      post.content,
      image_url:    post.image_url,
      link:         post.link,
      platforms:    post.platforms,
      scheduled_at: post.scheduled_at
        ? new Date(post.scheduled_at).toISOString().slice(0, 16)
        : "",
    });
    setPostDrawer(true);
  };

  const togglePlatform = (key) =>
    setPostForm((f) => ({
      ...f,
      platforms: f.platforms.includes(key)
        ? f.platforms.filter((p) => p !== key)
        : [...f.platforms, key],
    }));

  const savePost = async () => {
    if (!postForm.content.trim()) return showToast("Post content is required", "error");
    if (!postForm.platforms.length) return showToast("Select at least one platform", "error");

    setSaving(true);
    try {
      const payload = {
        ...postForm,
        scheduled_at: postForm.scheduled_at || null,
      };

      let res;
      if (editingPost) {
        res = await axios.put(`${apiUrl}/social-media/posts`, { id: editingPost.id, ...payload });
      } else {
        res = await axios.post(`${apiUrl}/social-media/posts`, payload);
      }

      if (res.data.status) {
        await load();
        setPostDrawer(false);
        showToast(editingPost ? "Post updated" : "Post created");
      } else {
        showToast(res.data.error || "Error saving post", "error");
      }
    } catch (e) {
      showToast("Network error", "error");
    } finally {
      setSaving(false);
    }
  };

  const deletePost = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    const res = await axios.delete(`${apiUrl}/social-media/posts?id=${id}`);
    if (res.data.status) { await load(); showToast("Post deleted"); }
    else showToast(res.data.error, "error");
  };

  const publishNow = async (post) => {
    setPublishing(post.id);
    try {
      const res = await axios.post(`${apiUrl}/social-media/posts/${post.id}/publish`);
      if (res.data.status) {
        await load();
        showToast("Published successfully");
      } else {
        showToast(res.data.error || "Publish failed", "error");
      }
    } catch (e) {
      showToast("Network error", "error");
    } finally {
      setPublishing(null);
    }
  };

  // ── Account CRUD ─────────────────────────────────────────────────────────

  const openAccount = (platform) => {
    const existing = accounts.find((a) => a.platform === platform);
    setAccountPlatform(platform);
    setAccountForm({
      account_name:    existing?.account_name    || "",
      platform_user_id: existing?.platform_user_id || "",
      access_token:    "", // always blank — user must re-enter
    });
    setAccountDrawer(true);
  };

  const saveAccount = async () => {
    if (!accountForm.platform_user_id.trim()) return showToast("Page/User ID is required", "error");
    if (!accountForm.access_token.trim()) return showToast("Access Token is required", "error");

    setSavingAccount(true);
    try {
      const res = await axios.post(`${apiUrl}/social-media/accounts`, {
        platform: accountPlatform,
        ...accountForm,
      });
      if (res.data.status) {
        await load();
        setAccountDrawer(false);
        showToast(`${accountPlatform} connected`);
      } else {
        showToast(res.data.error || "Error saving account", "error");
      }
    } catch (e) {
      showToast("Network error", "error");
    } finally {
      setSavingAccount(false);
    }
  };

  const disconnectAccount = async (platform) => {
    if (!window.confirm(`Disconnect ${platform}?`)) return;
    const res = await axios.delete(`${apiUrl}/social-media/accounts?platform=${platform}`);
    if (res.data.status) { await load(); showToast(`${platform} disconnected`); }
  };

  // ── Computed ──────────────────────────────────────────────────────────────

  const connectedCount = accounts.filter((a) => a.status === "connected").length;
  const publishedCount = posts.filter((p) => p.status === "published").length;
  const scheduledCount = posts.filter((p) => p.status === "scheduled").length;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      <Toast msg={toast.msg} type={toast.type} />

      {/* Header */}
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900">Social Media</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Automate posts across Facebook, Instagram, X, and LinkedIn
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className={btn("bg-white border border-gray-200 text-gray-600 hover:bg-gray-50")}>
            <RefreshCw size={15} /> Refresh
          </button>
          <button onClick={openAdd} className={btn("bg-orange-500 hover:bg-orange-600 text-white")}>
            <Plus size={15} /> New Post
          </button>
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: "Connected",  value: connectedCount, color: "text-emerald-600" },
          { label: "Total Posts", value: posts.length,   color: "text-gray-800"   },
          { label: "Published",  value: publishedCount, color: "text-blue-600"   },
          { label: "Scheduled",  value: scheduledCount, color: "text-yellow-600" },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
            <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">{label}</p>
            <p className={`text-3xl font-extrabold mt-1 ${color}`}>{value}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {[["posts","Posts"],["accounts","Platform Accounts"]].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors
              ${tab === key ? "bg-white shadow text-gray-900" : "text-gray-500 hover:text-gray-700"}`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Loading */}
      {loading ? (
        <div className="flex items-center gap-2 text-gray-400 py-8">
          <Loader size={18} className="animate-spin" /> Loading...
        </div>
      ) : tab === "accounts" ? (
        <AccountsTab accounts={accounts} onConnect={openAccount} onDisconnect={disconnectAccount} />
      ) : (
        <PostsTab
          posts={posts}
          publishing={publishing}
          onEdit={openEdit}
          onDelete={deletePost}
          onPublish={publishNow}
          onPreview={setPreview}
        />
      )}

      {/* Post drawer */}
      <Drawer open={postDrawer} onClose={() => setPostDrawer(false)} title={editingPost ? "Edit Post" : "Create Post"}>
        <div className="space-y-5">

          <Field label="Content *">
            <textarea
              className={`${inp} resize-none`}
              rows={5}
              placeholder="What would you like to share?"
              value={postForm.content}
              onChange={(e) => setPostForm((f) => ({ ...f, content: e.target.value }))}
            />
            <p className="text-xs text-gray-400 mt-1">{postForm.content.length} / 2200 chars</p>
          </Field>

          <Field label="Platforms *">
            <div className="flex flex-wrap gap-2 mt-1">
              {PLATFORMS.map((p) => (
                <PlatformChip
                  key={p.key}
                  platform={p.key}
                  selected={postForm.platforms.includes(p.key)}
                  onClick={() => togglePlatform(p.key)}
                />
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-1">Only connected platforms will actually post.</p>
          </Field>

          <Field label="Image URL" hint="Direct image URL (HTTPS). Instagram requires an image.">
            <div className="relative">
              <ImageIcon size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="url"
                className={`${inp} pl-8`}
                placeholder="https://example.com/image.jpg"
                value={postForm.image_url}
                onChange={(e) => setPostForm((f) => ({ ...f, image_url: e.target.value }))}
              />
            </div>
          </Field>

          <Field label="Link (optional)" hint="A URL to attach to the post (Facebook/LinkedIn).">
            <div className="relative">
              <Link2 size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="url"
                className={`${inp} pl-8`}
                placeholder="https://example.com"
                value={postForm.link}
                onChange={(e) => setPostForm((f) => ({ ...f, link: e.target.value }))}
              />
            </div>
          </Field>

          <Field label="Schedule (optional)" hint="Leave blank to save as Draft. Set a time to auto-publish.">
            <input
              type="datetime-local"
              className={inp}
              value={postForm.scheduled_at}
              onChange={(e) => setPostForm((f) => ({ ...f, scheduled_at: e.target.value }))}
            />
          </Field>

          <div className="flex gap-3 pt-2">
            <button
              onClick={savePost}
              disabled={saving}
              className={btn("bg-orange-500 hover:bg-orange-600 text-white flex-1 justify-center")}
            >
              {saving ? <Loader size={15} className="animate-spin" /> : <Plus size={15} />}
              {saving ? "Saving…" : editingPost ? "Save Changes" : "Create Post"}
            </button>
            <button onClick={() => setPostDrawer(false)} className={btn("bg-gray-100 hover:bg-gray-200 text-gray-700")}>
              Cancel
            </button>
          </div>
        </div>
      </Drawer>

      {/* Account drawer */}
      <Drawer
        open={accountDrawer}
        onClose={() => setAccountDrawer(false)}
        title={`Connect ${accountPlatform.charAt(0).toUpperCase() + accountPlatform.slice(1)}`}
      >
        <AccountForm
          platform={accountPlatform}
          form={accountForm}
          setForm={setAccountForm}
          onSave={saveAccount}
          onCancel={() => setAccountDrawer(false)}
          saving={savingAccount}
          inp={inp}
          btn={btn}
        />
      </Drawer>

      {/* Preview modal */}
      {preview && <PreviewModal post={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}

// ── Accounts Tab ──────────────────────────────────────────────────────────

function AccountsTab({ accounts, onConnect, onDisconnect }) {
  const map = {};
  accounts.forEach((a) => { map[a.platform] = a; });

  return (
    <div className="grid sm:grid-cols-2 gap-4">
      {PLATFORMS.map(({ key, label, Icon, color, text }) => {
        const acc = map[key];
        const connected = acc?.status === "connected";
        return (
          <div key={key} className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm space-y-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 ${color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                <Icon size={20} className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-gray-900">{label}</p>
                {connected && acc.account_name && (
                  <p className="text-xs text-gray-500 truncate">{acc.account_name}</p>
                )}
                {connected && acc.platform_user_id && (
                  <p className="text-xs text-gray-400 truncate">ID: {acc.platform_user_id}</p>
                )}
              </div>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full
                ${connected ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                {connected ? "Connected" : "Disconnected"}
              </span>
            </div>

            {connected ? (
              <div className="flex gap-2">
                <button
                  onClick={() => onConnect(key)}
                  className="flex-1 text-xs font-medium border border-gray-200 rounded-lg py-1.5 hover:bg-gray-50 transition"
                >
                  Update Credentials
                </button>
                <button
                  onClick={() => onDisconnect(key)}
                  className="text-xs font-medium border border-red-200 text-red-600 rounded-lg px-3 py-1.5 hover:bg-red-50 transition"
                >
                  Disconnect
                </button>
              </div>
            ) : (
              <button
                onClick={() => onConnect(key)}
                className={`w-full text-sm font-semibold py-2 rounded-xl text-white transition ${color} hover:opacity-90`}
              >
                Connect {label}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Account Form (inside drawer) ──────────────────────────────────────────

function AccountForm({ platform, form, setForm, onSave, onCancel, saving, inp, btn }) {
  const F = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const p = PLATFORMS.find((x) => x.key === platform) || {};

  const hints = {
    facebook:  { id: "Facebook Page ID (numeric)",           token: "Page Access Token from Meta Developer Portal" },
    instagram: { id: "Instagram Business Account ID",        token: "Instagram Access Token (via Meta Graph API)" },
    x:         { id: "Organization / User ID (optional)",    token: "Twitter Bearer Token (from developer.twitter.com)" },
    linkedin:  { id: "LinkedIn Organization ID (numeric)",   token: "LinkedIn Access Token (OAuth 2.0)" },
  };

  const h = hints[platform] || {};

  return (
    <div className="space-y-5">
      <div className={`flex items-center gap-3 p-4 ${p.color || "bg-gray-500"} rounded-xl text-white`}>
        {p.Icon && <p.Icon size={22} />}
        <div>
          <p className="font-bold">{p.label}</p>
          <p className="text-xs opacity-80">Enter your API credentials below</p>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Account Name (display label)</label>
          <input className={inp} placeholder="e.g. My Business Page" value={form.account_name} onChange={F("account_name")} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Page / User ID *</label>
          <input className={inp} placeholder={h.id || "Enter ID"} value={form.platform_user_id} onChange={F("platform_user_id")} />
          <p className="text-xs text-gray-400 mt-1">{h.id}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Access Token *</label>
          <textarea
            className={`${inp} resize-none font-mono text-xs`}
            rows={4}
            placeholder={h.token || "Paste your access token"}
            value={form.access_token}
            onChange={F("access_token")}
          />
          <p className="text-xs text-gray-400 mt-1">{h.token}</p>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700 space-y-1">
        <p className="font-semibold">Security note</p>
        <p>Tokens are stored server-side and never returned to the browser after saving. Revoke access in the platform's developer portal if compromised.</p>
      </div>

      <div className="flex gap-3">
        <button
          onClick={onSave}
          disabled={saving}
          className={btn(`${p.color || "bg-orange-500"} text-white hover:opacity-90 flex-1 justify-center`)}
        >
          {saving ? <Loader size={15} className="animate-spin" /> : <CheckCircle size={15} />}
          {saving ? "Connecting…" : "Save & Connect"}
        </button>
        <button onClick={onCancel} className={btn("bg-gray-100 hover:bg-gray-200 text-gray-700")}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Posts Tab ─────────────────────────────────────────────────────────────

function PostsTab({ posts, publishing, onEdit, onDelete, onPublish, onPreview }) {
  const [filter, setFilter] = useState("all");

  const filtered = posts.filter((p) => filter === "all" || p.status === filter);

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="flex flex-wrap gap-2">
        {["all","draft","scheduled","published","failed"].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-3 py-1 rounded-full text-xs font-medium capitalize transition-colors
              ${filter === s ? "bg-orange-500 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
          >
            {s}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Share2 size={40} className="mx-auto mb-3 opacity-30" />
          <p className="font-medium">No posts yet</p>
          <p className="text-sm">Create your first post to get started.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((post) => (
            <PostCard
              key={post.id}
              post={post}
              publishing={publishing === post.id}
              onEdit={() => onEdit(post)}
              onDelete={() => onDelete(post.id)}
              onPublish={() => onPublish(post)}
              onPreview={() => onPreview(post)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Post Card ─────────────────────────────────────────────────────────────

function PostCard({ post, publishing, onEdit, onDelete, onPublish, onPreview }) {
  const [expanded, setExpanded] = useState(false);

  const canPublish = post.status === "draft" || post.status === "scheduled" || post.status === "failed";

  return (
    <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
      <div className="p-4">
        <div className="flex items-start gap-3">
          {/* Platform icons */}
          <div className="flex gap-1 flex-shrink-0 mt-0.5">
            {(post.platforms || []).map((key) => {
              const p = PLATFORMS.find((x) => x.key === key);
              if (!p) return null;
              const result = post.platform_results?.[key];
              return (
                <div
                  key={key}
                  title={result?.error ? `Error: ${result.error}` : result?.post_id ? "Published" : p.label}
                  className={`w-7 h-7 rounded-lg flex items-center justify-center
                    ${result?.error ? "bg-red-100" : result?.post_id ? p.color : "bg-gray-100"}`}
                >
                  <p.Icon
                    size={13}
                    className={result?.error ? "text-red-500" : result?.post_id ? "text-white" : "text-gray-400"}
                  />
                </div>
              );
            })}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <p className={`text-sm text-gray-800 ${!expanded ? "line-clamp-2" : ""}`}>
              {post.content}
            </p>
            {post.content.length > 120 && (
              <button
                onClick={() => setExpanded((e) => !e)}
                className="text-xs text-orange-500 hover:underline mt-0.5"
              >
                {expanded ? "Show less" : "Show more"}
              </button>
            )}
          </div>

          {/* Status badge */}
          <span className={`flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0
            ${STATUS_BADGE[post.status] || "bg-gray-100 text-gray-600"}`}>
            {STATUS_ICON[post.status]}
            {post.status}
          </span>
        </div>

        {/* Meta row */}
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-xs text-gray-400">
          {post.image_url && <span className="flex items-center gap-1"><ImageIcon size={11} /> Has image</span>}
          {post.link && <span className="flex items-center gap-1"><Link2 size={11} /> Has link</span>}
          {post.scheduled_at && (
            <span className="flex items-center gap-1">
              <Clock size={11} /> Scheduled: {new Date(post.scheduled_at).toLocaleString()}
            </span>
          )}
          {post.published_at && (
            <span className="flex items-center gap-1">
              <CheckCircle size={11} /> Published: {new Date(post.published_at).toLocaleString()}
            </span>
          )}
          <span>#{post.id}</span>
        </div>

        {/* Actions */}
        <div className="flex gap-2 mt-3">
          {canPublish && (
            <button
              onClick={onPublish}
              disabled={publishing}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-xs font-medium transition"
            >
              {publishing ? <Loader size={12} className="animate-spin" /> : <Send size={12} />}
              {publishing ? "Publishing…" : "Publish Now"}
            </button>
          )}
          <button
            onClick={onPreview}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-50 transition"
          >
            <Eye size={12} /> Preview
          </button>
          <button
            onClick={onEdit}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-50 transition"
          >
            <Edit2 size={12} /> Edit
          </button>
          <button
            onClick={onDelete}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-red-100 rounded-lg text-xs font-medium text-red-500 hover:bg-red-50 transition ml-auto"
          >
            <Trash2 size={12} /> Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Preview Modal ─────────────────────────────────────────────────────────

function PreviewModal({ post, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h3 className="font-bold text-gray-900">Post Preview</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-full"><X size={16} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Platforms */}
          <div className="flex gap-2 flex-wrap">
            {(post.platforms || []).map((key) => {
              const p = PLATFORMS.find((x) => x.key === key);
              if (!p) return null;
              return (
                <span key={key} className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold text-white ${p.color}`}>
                  <p.Icon size={11} /> {p.label}
                </span>
              );
            })}
          </div>

          {/* Mock post card */}
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            {post.image_url && (
              <img src={post.image_url} alt="post" className="w-full object-cover max-h-52" onError={(e) => { e.target.style.display = "none"; }} />
            )}
            <div className="p-4">
              <p className="text-sm text-gray-800 whitespace-pre-wrap">{post.content}</p>
              {post.link && (
                <a href={post.link} target="_blank" rel="noreferrer"
                   className="mt-2 text-xs text-blue-600 underline flex items-center gap-1">
                  <Link2 size={11} /> {post.link}
                </a>
              )}
            </div>
          </div>

          {/* Platform results */}
          {Object.keys(post.platform_results || {}).length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Publish Results</p>
              {Object.entries(post.platform_results).map(([plat, result]) => {
                const p = PLATFORMS.find((x) => x.key === plat);
                return (
                  <div key={plat} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs
                    ${result.error ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}>
                    {p && <p.Icon size={13} />}
                    <span className="font-medium capitalize">{plat}</span>
                    <span className="ml-auto">
                      {result.error ? `Error: ${result.error}` : `ID: ${result.post_id}`}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
